class SessionService {
    constructor() {
        this.SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
        this.STORAGE_KEYS = {
            TOKEN: 'token',
            USER_ROLE: 'userRole',
            USER: 'user',
            LAST_ACTIVITY: 'lastActivity'
        };
        
        this.sessionCheckInterval = null;
        this.sessionExpiredCallback = null;
        // Event system for session state changes
        this.listeners = {
            sessionExpired: new Set(),
            loggedIn: new Set(),
            loggedOut: new Set()
        };
        
        // Bind methods to maintain context
        this.handleStorageChange = this.handleStorageChange.bind(this);
        
        // Listen to storage events for multi-tab synchronization
        window.addEventListener('storage', this.handleStorageChange);
    }

    // Event handling methods
    addEventListener(event, callback) {
        if (this.listeners[event]) {
            this.listeners[event].add(callback);
            return true;
        }
        return false;
    }

    removeEventListener(event, callback) {
        if (this.listeners[event]) {
            this.listeners[event].delete(callback);
            return true;
        }
        return false;
    }

    emitEvent(event) {
        if (this.listeners[event]) {
            this.listeners[event].forEach(callback => callback());
        }
    }

    handleStorageChange(event) {
        // Handle storage changes for multi-tab synchronization
        if (event.key === this.STORAGE_KEYS.TOKEN) {
            if (!event.newValue && event.oldValue) {
                this.emitEvent('loggedOut');
            } else if (event.newValue && !event.oldValue) {
                this.emitEvent('loggedIn');
            }
        }
    }

    startSessionCheck(callback) {
        this.sessionExpiredCallback = callback;
        if (this.sessionCheckInterval) {
            clearInterval(this.sessionCheckInterval);
        }
        
        this.sessionCheckInterval = setInterval(() => {
            if (!this.isSessionValid()) {
                this.handleSessionExpiration();
            }
        }, 60000); // Check every minute
    }

    stopSessionCheck() {
        if (this.sessionCheckInterval) {
            clearInterval(this.sessionCheckInterval);
            this.sessionCheckInterval = null;
        }
    }

    handleSessionExpiration() {
        this.emitEvent('sessionExpired');
        this.logout().then(() => {
            if (this.sessionExpiredCallback) {
                this.sessionExpiredCallback();
            }
        });
    }

    login(token, role, userData) {
        localStorage.setItem(this.STORAGE_KEYS.TOKEN, token);
        localStorage.setItem(this.STORAGE_KEYS.USER_ROLE, role);
        localStorage.setItem(this.STORAGE_KEYS.USER, JSON.stringify(userData));
        this.updateLastActivity();
        this.emitEvent('loggedIn');
    }

    async logout() {
        Object.values(this.STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
        sessionStorage.clear();
        this.stopSessionCheck();
        this.emitEvent('loggedOut');
        return true;
    }

    isSessionValid() {
        const token = this.getToken();
        const lastActivity = localStorage.getItem(this.STORAGE_KEYS.LAST_ACTIVITY);
        
        if (!token || !lastActivity) {
            return false;
        }
        
        const isExpired = Date.now() - parseInt(lastActivity) > this.SESSION_TIMEOUT;
        return !isExpired;
    }

    updateLastActivity() {
        localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
    }

    getToken() {
        return localStorage.getItem(this.STORAGE_KEYS.TOKEN);
    }

    getUserRole() {
        return localStorage.getItem(this.STORAGE_KEYS.USER_ROLE);
    }

    getUser() {
        const userData = localStorage.getItem(this.STORAGE_KEYS.USER);
        return userData ? JSON.parse(userData) : null;
    }

    cleanup() {
        window.removeEventListener('storage', this.handleStorageChange);
        this.stopSessionCheck();
        Object.keys(this.listeners).forEach(event => {
            this.listeners[event].clear();
        });
    }
}

export const sessionService = new SessionService();